import { apiClient } from './client'

// Types
export type PaymentConfigStatus = 'PENDING' | 'VERIFIED' | 'REJECTED' | 'SUSPENDED'
export type AccountType = 'wallet' | 'invoice'

export interface Bank {
  code: string
  name: string
}

export interface BankAccount {
  bankCode: string
  bankName: string
  accountNumber: string
}

export interface PaymentConfig {
  id: string
  institutionId: string
  accountType: AccountType
  bankAccount: BankAccount
  status: PaymentConfigStatus
  isActive: boolean
  verifiedAt?: string
  createdBy?: { _id: string; firstName: string; lastName: string; email: string }
  verifiedBy?: { _id: string; firstName: string; lastName: string; email: string }
  updatedBy?: { _id: string; firstName: string; lastName: string; email: string }
  createdAt: string
  updatedAt: string
}

export interface PaymentConfigsResponse {
  configs: PaymentConfig[]
  walletConfig: PaymentConfig | null
  invoiceConfig: PaymentConfig | null
}

export interface InitiateConfigResponse {
  configId: string
  accountType: AccountType
  email: string
  expiresIn: number
}

export interface VerifyConfigResponse {
  id: string
  institutionId: string
  accountType: AccountType
  bankAccount: {
    bankCode: string
    bankName: string
    accountNumber: string
  }
  status: PaymentConfigStatus
  isActive: boolean
  verifiedAt: string
}

// ============================================
// PAYMENT CONFIG API
// ============================================

export const paymentConfigApi = {
  // Get list of banks from external API
  getBanks: async () => {
    const response = await apiClient.get<{
      success: boolean
      data: Bank[]
      message: string
      warning?: string
    }>('/payment-config/banks')
    return response.data
  },

  // Get all payment configurations (optionally filter by accountType)
  getPaymentConfig: async (accountType?: AccountType) => {
    const params = accountType ? `?accountType=${accountType}` : ''
    const response = await apiClient.get<{
      success: boolean
      data: PaymentConfigsResponse
      message: string
    }>(`/payment-config${params}`)
    return response.data
  },

  // Initiate payment configuration (Step 1)
  initiatePaymentConfig: async (data: {
    bankCode: string
    bankName: string
    accountNumber: string
    accountType: AccountType
  }) => {
    const response = await apiClient.post<{
      success: boolean
      data: InitiateConfigResponse
      message: string
    }>('/payment-config/initiate', data)
    return response.data
  },

  // Verify OTP and activate configuration (Step 2)
  verifyPaymentConfig: async (configId: string, otp: string) => {
    const response = await apiClient.post<{
      success: boolean
      data: VerifyConfigResponse
      message: string
    }>('/payment-config/verify', { configId, otp })
    return response.data
  },

  // Resend OTP
  resendOTP: async (configId: string) => {
    const response = await apiClient.post<{
      success: boolean
      data: { email: string; expiresIn: number }
      message: string
    }>('/payment-config/resend-otp', { configId })
    return response.data
  },

  // Deactivate payment configuration
  deactivatePaymentConfig: async (configId: string, reason?: string) => {
    const response = await apiClient.post<{
      success: boolean
      data: { id: string; accountType: AccountType }
      message: string
    }>('/payment-config/deactivate', { configId, reason })
    return response.data
  },

  // Initiate STK Push payment (M-Pesa)
  initiateStkPush: async (data: {
    amount: number
    phoneNumber: string
    orderId: string
    type?: 'wallet' | 'invoice'
  }) => {
    const response = await apiClient.post<{
      success: boolean
      data: {
        paymentRef: string
        checkoutRequestId: string | null
        merchantRequestId: string | null
      }
      message: string
    }>('/payment-config/stk-push', data)
    return response.data
  },
}

export default paymentConfigApi
